/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * PCG playback listening filter — a faithful port of the prototype viewer's
 * Web Audio graph (Medaica_PCG_Engineering_Handoff_v0.8.1-dev.17,
 * `ensurePlaybackFilter` / `setPlaybackFilter` / `setPlaybackCustomBand`,
 * with the v0.9.1 level-compensated presets).
 *
 * Topology (identical to the prototype — acoustic parity is the point):
 *
 *   source ─┬─ dryGain ──────────────────────────────────────┬─ levelGain ─ destination
 *           └─ HP(Q=0.5411961) ─ HP(Q=1.306563)              │
 *                └─ LP(Q=0.5411961) ─ LP(Q=1.306563) ─ wetGain ──┘
 *
 * i.e. a 4th-order Butterworth band-pass built as two cascaded high-pass
 * biquad sections followed by two cascaded low-pass biquad sections, with
 * parallel dry/wet gains cross-faded via setTargetAtTime (τ = 15 ms) so
 * preset changes are click-free. "Off" is dry=1/wet=0 — the filter chain
 * stays wired but silent.
 *
 * Level compensation (upstream v0.9.1 `playbackMasterGain` +
 * `playbackFilterLoudnessGain`): a narrow band is quieter than the whole
 * recording, so each preset carries a gain that brings it back to the
 * wide-band level. The per-recording gains come from the guide payload
 * (`playbackFilterLoudness.gains`, measured by the pipeline as the ratio of
 * the wide-band RMS to the band's RMS); guides without the key use upstream's
 * fallback table. The gain is applied on its own GainNode after the dry/wet
 * sum, inside this chain, so the host graph's loop-boundary fades (which
 * reset the host's master gain to 1) never disturb it.
 *
 * Playback-only guarantee: this module touches the audio output graph only.
 * It must never feed analysis inputs (see the handoff's
 * `engineering_handoff_v081_dev17.md`, "Engineering cautions": "Playback
 * filters affect listening only and must not recalculate results").
 */

type PcgFilterPreset = "off" | "wide" | "low" | "high" | "custom"

/** [highPassHz, lowPassHz] — i.e. [band low edge, band high edge] */
type PcgFilterBand = [number, number]

/** Preset bands exactly as shipped in the prototype (`setPlaybackFilter`, v0.9.1). */
const PCG_FILTER_PRESET_BANDS: { [key: string]: PcgFilterBand } = {
  wide: [35, 1200],
  low: [35, 200],
  high: [150, 1000],
}

/** Level-compensation gain per preset, keyed like the payload's `gains`. */
type PcgFilterLevelGains = { [preset in PcgFilterPreset]?: number }

/**
 * Upstream's fallback table (`playbackFilterLoudnessGain`) for guides that
 * carry no `playbackFilterLoudness` — every guide produced before the
 * pipeline measured the gains, and the custom band, which is never measured.
 */
const PCG_FILTER_FALLBACK_LEVEL_GAINS: Required<PcgFilterLevelGains> = {
  off: 1,
  wide: 1,
  low: 1.1,
  high: 2.4,
  custom: 1.5,
}

/** Upstream re-clamps every payload gain to this range before applying it. */
const PCG_FILTER_LEVEL_GAIN_RANGE: [number, number] = [0.5, 4]

/**
 * The level-compensation gains for a guide payload (upstream
 * `playbackFilterLoudnessGain`, resolved for every preset at once): the
 * payload's `playbackFilterLoudness.gains` (or the exam package's
 * `audio.playback_filter_loudness.gains`) re-clamped to [0.5, 4], with the
 * fallback table filling every preset the payload does not measure.
 */
const playbackFilterLevelGains = (payload: unknown): Required<PcgFilterLevelGains> => {
  const data = (payload || {}) as {
    playbackFilterLoudness?: { gains?: Record<string, unknown> } | null
    examPackage?: { audio?: { playback_filter_loudness?: { gains?: Record<string, unknown> } | null } | null } | null
  }
  const loudness = data.playbackFilterLoudness || data.examPackage?.audio?.playback_filter_loudness || {}
  const gains = loudness?.gains || {}
  const resolved = { ...PCG_FILTER_FALLBACK_LEVEL_GAINS }
  ;(Object.keys(resolved) as PcgFilterPreset[]).forEach((preset) => {
    const value = Number(gains[preset])
    if (Number.isFinite(value) && value > 0) {
      resolved[preset] = Math.max(PCG_FILTER_LEVEL_GAIN_RANGE[0], Math.min(PCG_FILTER_LEVEL_GAIN_RANGE[1], value))
    }
  })
  return resolved
}

/** Two-section decomposition of a 4th-order Butterworth response. */
const BUTTERWORTH_SECTION_Q: [number, number] = [0.5411961, 1.306563]

/** Cross-fade / frequency-glide time constant used throughout the prototype. */
const SMOOTHING_TIME_CONSTANT_S = 0.015

const DEFAULT_CUSTOM_BAND: PcgFilterBand = [20, 1200]

class PcgPlaybackFilterGraph {
  private readonly _ctx: BaseAudioContext
  private readonly _source: AudioNode
  private readonly _destination: AudioNode
  private readonly _dryGain: GainNode
  private readonly _wetGain: GainNode
  private readonly _levelGain: GainNode
  private readonly _highPassNodes: BiquadFilterNode[]
  private readonly _lowPassNodes: BiquadFilterNode[]
  private _preset: PcgFilterPreset = "off"
  private _customBand: PcgFilterBand = DEFAULT_CUSTOM_BAND
  private _levelGains: Required<PcgFilterLevelGains> = { ...PCG_FILTER_FALLBACK_LEVEL_GAINS }

  constructor(ctx: BaseAudioContext, source: AudioNode, destination?: AudioNode) {
    this._ctx = ctx
    this._source = source
    this._destination = destination ?? ctx.destination

    this._dryGain = ctx.createGain()
    this._wetGain = ctx.createGain()
    this._levelGain = ctx.createGain()
    this._dryGain.gain.value = 1
    this._wetGain.gain.value = 0
    this._levelGain.gain.value = this._levelGains.off

    this._highPassNodes = BUTTERWORTH_SECTION_Q.map((q) => {
      const node = ctx.createBiquadFilter()
      node.type = "highpass"
      node.Q.value = q
      return node
    })
    this._lowPassNodes = BUTTERWORTH_SECTION_Q.map((q) => {
      const node = ctx.createBiquadFilter()
      node.type = "lowpass"
      node.Q.value = q
      return node
    })

    // Dry path
    this._source.connect(this._dryGain)
    this._dryGain.connect(this._levelGain)

    // Wet path: HP → HP → LP → LP → wet
    this._source.connect(this._highPassNodes[0])
    this._highPassNodes[0].connect(this._highPassNodes[1])
    this._highPassNodes[1].connect(this._lowPassNodes[0])
    this._lowPassNodes[0].connect(this._lowPassNodes[1])
    this._lowPassNodes[1].connect(this._wetGain)
    this._wetGain.connect(this._levelGain)

    // Level compensation after the dry/wet sum, before the host's output
    this._levelGain.connect(this._destination)
  }

  get preset(): PcgFilterPreset {
    return this._preset
  }

  get customBand(): PcgFilterBand {
    return this._customBand
  }

  /** The level-compensation gain currently applied (1 while bypassed). */
  get levelGain(): number {
    return this._levelGains[this._preset]
  }

  /**
   * Install the recording's level-compensation gains (see
   * playbackFilterLevelGains) and re-apply the one for the current preset,
   * so a recording change retunes the level without a preset change.
   */
  setLevelGains(gains: PcgFilterLevelGains | null | undefined): void {
    this._levelGains = { ...PCG_FILTER_FALLBACK_LEVEL_GAINS, ...gains }
    this._applyLevelGain()
  }

  /** The band currently audible, or null when the filter is bypassed. */
  get activeBand(): PcgFilterBand | null {
    if (this._preset === "off") {
      return null
    }
    if (this._preset === "custom") {
      return this._customBand
    }
    return PCG_FILTER_PRESET_BANDS[this._preset]
  }

  setPreset(preset: "off" | "wide" | "low" | "high"): void {
    const now = this._ctx.currentTime
    if (preset === "off") {
      this._preset = "off"
      this._dryGain.gain.setTargetAtTime(1, now, SMOOTHING_TIME_CONSTANT_S)
      this._wetGain.gain.setTargetAtTime(0, now, SMOOTHING_TIME_CONSTANT_S)
      this._applyLevelGain()
      return
    }
    const band = PCG_FILTER_PRESET_BANDS[preset]
    if (!band) {
      return
    }
    this._preset = preset
    this._applyBand(band)
  }

  /**
   * Dual-thumb custom band. Returns false (and changes nothing) when the
   * band is not a valid low < high pair — mirroring the prototype's
   * `setPlaybackCustomBand` contract.
   */
  setCustomBand(lowHz: number, highHz: number): boolean {
    if (!Number.isFinite(Number(lowHz)) || !Number.isFinite(Number(highHz)) || Number(lowHz) >= Number(highHz)) {
      return false
    }
    this._preset = "custom"
    this._customBand = [Number(lowHz), Number(highHz)]
    this._applyBand(this._customBand)
    return true
  }

  private _applyBand([lowHz, highHz]: PcgFilterBand): void {
    const now = this._ctx.currentTime
    this._highPassNodes.forEach((node) => node.frequency.setTargetAtTime(lowHz, now, SMOOTHING_TIME_CONSTANT_S))
    this._lowPassNodes.forEach((node) => node.frequency.setTargetAtTime(highHz, now, SMOOTHING_TIME_CONSTANT_S))
    this._dryGain.gain.setTargetAtTime(0, now, SMOOTHING_TIME_CONSTANT_S)
    this._wetGain.gain.setTargetAtTime(1, now, SMOOTHING_TIME_CONSTANT_S)
    this._applyLevelGain()
  }

  private _applyLevelGain(): void {
    this._levelGain.gain.setTargetAtTime(this.levelGain, this._ctx.currentTime, SMOOTHING_TIME_CONSTANT_S)
  }

  /**
   * Disconnects every node this graph created. Does NOT disconnect or close
   * the source/context — the caller owns those (a MediaElementSourceNode can
   * only ever be created once per media element, so the owner must decide
   * its fate).
   */
  dispose(): void {
    this._source.disconnect(this._dryGain)
    this._source.disconnect(this._highPassNodes[0])
    this._dryGain.disconnect()
    this._wetGain.disconnect()
    this._levelGain.disconnect()
    this._highPassNodes.forEach((node) => node.disconnect())
    this._lowPassNodes.forEach((node) => node.disconnect())
  }
}

export default PcgPlaybackFilterGraph
export {
  PCG_FILTER_PRESET_BANDS,
  PCG_FILTER_FALLBACK_LEVEL_GAINS,
  PCG_FILTER_LEVEL_GAIN_RANGE,
  BUTTERWORTH_SECTION_Q,
  SMOOTHING_TIME_CONSTANT_S,
  DEFAULT_CUSTOM_BAND,
  playbackFilterLevelGains,
}
export type { PcgFilterPreset, PcgFilterBand, PcgFilterLevelGains }
